import * as amplitude from "@amplitude/analytics-browser";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useHtmlEventListener } from "@redotech/react-util/event";
import { useHandler } from "@redotech/react-util/hook";
import LayoutLeft from "@redotech/redo-icons/layout-left.svg";
import LayoutLeftFilled from "@redotech/redo-icons/layout-left_filled.svg";
import PhoneIcon from "@redotech/redo-icons/phone.svg";
import PhoneFilledIcon from "@redotech/redo-icons/phone_filled.svg";
import GearIcon from "@redotech/redo-icons/settings-02.svg";
import GearFilledIcon from "@redotech/redo-icons/settings-02_filled.svg";
import classNames from "classnames";
import {
  createContext,
  JSXElementConstructor,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { Flex } from "../../../flex";
import * as darkModeCss from "../../../theme/component-colors-dark.module.css";
import { usePhoneWidget } from "../../../voice/phone-widget-context";
import { RedoNavigationTeamItem } from "../common/redo-nav-team-item";
import { UserProfile } from "../common/redo-nav-user-profile";
import * as navigationCss from "../redo-navigation.module.css";
import { PrimaryNavigationTab } from "./redo-primary-nav-tab";

interface BasePrimaryTab {
  id: string;
  hasNotification: boolean;
  UnfilledIcon: JSXElementConstructor<any>;
  FilledIcon: JSXElementConstructor<any>;
  friendlyName: string;
  hideLabel?: boolean;
}

export interface LinkPrimaryTab extends BasePrimaryTab {
  type: "link";
  href: string;
  pathRoot: string;
}

interface ButtonPrimaryTab extends BasePrimaryTab {
  type: "button";
  onClick: () => void;
}

export type PrimaryTab = LinkPrimaryTab | ButtonPrimaryTab;

interface PrimaryTabSections {
  core: LinkPrimaryTab[];
  activeProducts: LinkPrimaryTab[];
  inactiveProducts: LinkPrimaryTab[];
}

interface RedoPrimaryNavProps {
  toggleCollapseMode: () => void;
  isCollapseMode: boolean;
  enterSettingsUrl: string | null;
  settingsRoot: string;
  pageTabs: LinkPrimaryTab[];
  primaryTabSections: PrimaryTabSections;
  selectedTabId: string | undefined;
  hideTeamSwitcher?: boolean;
  /**
   * When true, render the pre-refactor flat primary nav layout (no
   * core/active/inactive sections, no drag-and-drop reordering, no
   * resizable width). This is the layout that ships when the
   * NEW_NAVIGATION feature flag is off so merchants on legacy nav see
   * exactly the same primary nav they did before the refactor.
   */
  useLegacyLayout?: boolean;
}

/**
 * Applies a stored order to a list of tabs. Tabs present in storedOrder
 * come first (in that order), followed by any new tabs not in the stored order.
 */
function applyStoredOrder(
  tabs: LinkPrimaryTab[],
  storedOrder: string[],
): LinkPrimaryTab[] {
  if (storedOrder.length === 0) {
    return tabs;
  }
  const tabMap = new Map(tabs.map((tab) => [tab.id, tab]));
  const ordered: LinkPrimaryTab[] = [];
  for (const id of storedOrder) {
    const tab = tabMap.get(id);
    if (tab) {
      ordered.push(tab);
      tabMap.delete(id);
    }
  }
  // Append any tabs not in stored order (new products)
  for (const tab of tabMap.values()) {
    ordered.push(tab);
  }
  return ordered;
}

const SortablePrimaryTab = memo(function SortablePrimaryTab({
  tab,
  lastDragTimeRef,
  selectedTabId,
}: {
  tab: LinkPrimaryTab;
  lastDragTimeRef: React.MutableRefObject<number>;
  selectedTabId: string | undefined;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
    // Block pointer events on the inner link while dragging
    pointerEvents: isDragging ? "none" : undefined,
  };

  const handleClickCapture = useCallback(
    (e: React.MouseEvent) => {
      // Block clicks for 500ms after any drag ends
      if (Date.now() - lastDragTimeRef.current < 500) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [lastDragTimeRef],
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClickCapture={handleClickCapture}
    >
      <PrimaryNavigationTab
        sectionType="active"
        selectedTabId={selectedTabId}
        tab={tab}
      />
    </div>
  );
});

const ACTIVE_NAV_ORDER_KEY = "redo_nav_order_active";

function readStoredOrder(): string[] {
  try {
    const raw = localStorage.getItem(ACTIVE_NAV_ORDER_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

const SortableActiveSection = memo(function SortableActiveSection({
  tabs,
  selectedTabId,
}: {
  tabs: LinkPrimaryTab[];
  selectedTabId: string | undefined;
}) {
  const [storedOrder, setStoredOrder] = useState<string[]>(readStoredOrder);
  const lastDragTimeRef = useRef(0);

  const orderedTabs = useMemo(
    () => applyStoredOrder(tabs, storedOrder),
    [tabs, storedOrder],
  );

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      lastDragTimeRef.current = Date.now();

      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const oldIndex = orderedTabs.findIndex((t) => t.id === active.id);
      const newIndex = orderedTabs.findIndex((t) => t.id === over.id);
      amplitude.track("nav/tab-reordered", {
        tab: active.id,
        fromIndex: oldIndex,
        toIndex: newIndex,
      });
      const reordered = arrayMove(orderedTabs, oldIndex, newIndex);
      const newOrder = reordered.map((t) => t.id);
      setStoredOrder(newOrder);
      localStorage.setItem(ACTIVE_NAV_ORDER_KEY, JSON.stringify(newOrder));
    },
    [orderedTabs],
  );

  const tabIds = useMemo(() => orderedTabs.map((t) => t.id), [orderedTabs]);

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <SortableContext items={tabIds} strategy={verticalListSortingStrategy}>
        <Flex dir="column" gap="lg">
          {orderedTabs.map((tab) => (
            <SortablePrimaryTab
              key={tab.id}
              lastDragTimeRef={lastDragTimeRef}
              selectedTabId={selectedTabId}
              tab={tab}
            />
          ))}
        </Flex>
      </SortableContext>
    </DndContext>
  );
});

const PRIMARY_NAV_WIDTH_KEY = "redo_primary_nav_width";
const DEFAULT_PRIMARY_NAV_WIDTH = 72;
const MIN_PRIMARY_NAV_WIDTH = 48;
const ICON_ONLY_THRESHOLD = 64;

function readStoredWidth(): number {
  try {
    const raw = localStorage.getItem(PRIMARY_NAV_WIDTH_KEY);
    if (raw) {
      const parsed = Number(raw);
      if (
        !Number.isNaN(parsed) &&
        parsed >= MIN_PRIMARY_NAV_WIDTH &&
        parsed <= DEFAULT_PRIMARY_NAV_WIDTH
      ) {
        return parsed;
      }
    }
  } catch {
    // ignore
  }
  return DEFAULT_PRIMARY_NAV_WIDTH;
}

export const PrimaryNavIconOnlyContext = createContext(false);

export const RedoPrimaryNav = memo(function RedoPrimaryNav({
  toggleCollapseMode,
  isCollapseMode,
  enterSettingsUrl,
  settingsRoot,
  pageTabs,
  primaryTabSections,
  selectedTabId,
  hideTeamSwitcher = false,
  useLegacyLayout = false,
}: RedoPrimaryNavProps) {
  if (useLegacyLayout) {
    return (
      <LegacyPrimaryNavLayout
        enterSettingsUrl={enterSettingsUrl}
        hideTeamSwitcher={hideTeamSwitcher}
        isCollapseMode={isCollapseMode}
        pageTabs={pageTabs}
        selectedTabId={selectedTabId}
        settingsRoot={settingsRoot}
        toggleCollapseMode={toggleCollapseMode}
      />
    );
  }

  return (
    <NewPrimaryNavLayout
      enterSettingsUrl={enterSettingsUrl}
      hideTeamSwitcher={hideTeamSwitcher}
      isCollapseMode={isCollapseMode}
      primaryTabSections={primaryTabSections}
      selectedTabId={selectedTabId}
      settingsRoot={settingsRoot}
      toggleCollapseMode={toggleCollapseMode}
    />
  );
});

const NewPrimaryNavLayout = memo(function NewPrimaryNavLayout({
  toggleCollapseMode,
  isCollapseMode,
  enterSettingsUrl,
  settingsRoot,
  primaryTabSections,
  selectedTabId,
  hideTeamSwitcher = false,
}: Omit<RedoPrimaryNavProps, "pageTabs" | "useLegacyLayout">) {
  const { isVisible, toggleVisibility, isVoiceAvailable } = usePhoneWidget();

  const [navWidth, setNavWidth] = useState(readStoredWidth);
  const [isResizing, setIsResizing] = useState(false);
  const isDragging = useRef(false);
  const lastX = useRef(0);

  const iconOnly = navWidth < ICON_ONLY_THRESHOLD;

  const handleMouseDown = useHandler((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    lastX.current = e.clientX;
    setIsResizing(true);
  });

  useHtmlEventListener(document.documentElement, "mousemove", (e) => {
    if (!isDragging.current) {
      return;
    }
    const delta = e.clientX - lastX.current;
    lastX.current = e.clientX;
    setNavWidth((prev) =>
      Math.max(
        MIN_PRIMARY_NAV_WIDTH,
        Math.min(DEFAULT_PRIMARY_NAV_WIDTH, prev + delta),
      ),
    );
  });

  useHtmlEventListener(document.documentElement, "mouseup", () => {
    if (!isDragging.current) {
      return;
    }
    isDragging.current = false;
    setIsResizing(false);
    setNavWidth((w) => {
      localStorage.setItem(PRIMARY_NAV_WIDTH_KEY, String(w));
      return w;
    });
  });

  return (
    <PrimaryNavIconOnlyContext.Provider value={iconOnly}>
      <Flex
        align="center"
        bgColor="tertiary"
        className={classNames(
          darkModeCss.darkMode,
          navigationCss.primaryNav,
          isResizing && navigationCss.primaryNavResizing,
        )}
        dir="column"
        grow={1}
        justify="space-between"
        px="xs"
        py="xl"
        style={{ width: navWidth, maxWidth: navWidth }}
      >
        <Flex align="center" dir="column" gap="lg">
          {!hideTeamSwitcher && <RedoNavigationTeamItem />}
          <Flex dir="column" overflowY="auto">
            <Flex
              dir="column"
              gap="lg"
              overflowY="auto"
              style={{ scrollbarWidth: "none" }}
            >
              {primaryTabSections.core.map((tab) => (
                <PrimaryNavigationTab
                  key={tab.id}
                  sectionType="core"
                  selectedTabId={selectedTabId}
                  tab={tab}
                />
              ))}
              {primaryTabSections.activeProducts.length > 0 && (
                <>
                  <div className={navigationCss.primaryNavDivider} />
                  <SortableActiveSection
                    selectedTabId={selectedTabId}
                    tabs={primaryTabSections.activeProducts}
                  />
                </>
              )}
              {primaryTabSections.inactiveProducts.length > 0 && (
                <>
                  <div className={navigationCss.primaryNavDivider} />
                  {primaryTabSections.inactiveProducts.map((tab) => (
                    <PrimaryNavigationTab
                      key={tab.id}
                      sectionType="inactive"
                      selectedTabId={selectedTabId}
                      tab={tab}
                    />
                  ))}
                </>
              )}
            </Flex>
          </Flex>
        </Flex>
        <Flex align="center" dir="column" gap="lg">
          <PrimaryNavigationTab
            manuallySelected={isCollapseMode}
            tab={{
              id: "collapse",
              UnfilledIcon: LayoutLeft,
              FilledIcon: LayoutLeftFilled,
              type: "button",
              friendlyName: "Collapse",
              hasNotification: false,
              hideLabel: true,
              onClick: toggleCollapseMode,
            }}
          />
          {isVoiceAvailable && (
            <PrimaryNavigationTab
              manuallySelected={isVisible}
              tab={{
                id: "phone",
                UnfilledIcon: PhoneIcon,
                FilledIcon: PhoneFilledIcon,
                type: "button",
                friendlyName: "Phone",
                hasNotification: false,
                hideLabel: true,
                onClick: toggleVisibility,
              }}
            />
          )}
          {enterSettingsUrl && (
            <PrimaryNavigationTab
              tab={{
                id: "settings",
                hasNotification: false,
                UnfilledIcon: GearIcon,
                FilledIcon: GearFilledIcon,
                type: "link",
                href: enterSettingsUrl,
                hideLabel: true,
                friendlyName: "Settings",
                pathRoot: settingsRoot,
              }}
            />
          )}
          <UserProfile collapsed />
        </Flex>
        <div
          className={navigationCss.primaryNavResizeHandle}
          data-resizing={isResizing || undefined}
          onMouseDown={handleMouseDown}
        />
      </Flex>
    </PrimaryNavIconOnlyContext.Provider>
  );
});

/**
 * Pre-refactor primary nav layout. Matches the main-branch version
 * byte-for-byte (minus the selectedTabId prop plumbing, which is
 * backwards-compatible): simple flat pageTabs.map, no sectioning, no
 * drag-and-drop, no resizable width, no localStorage persistence.
 * Rendered when `useLegacyLayout` is true.
 */
const LegacyPrimaryNavLayout = memo(function LegacyPrimaryNavLayout({
  toggleCollapseMode,
  isCollapseMode,
  enterSettingsUrl,
  settingsRoot,
  pageTabs,
  selectedTabId,
  hideTeamSwitcher = false,
}: Omit<RedoPrimaryNavProps, "primaryTabSections" | "useLegacyLayout">) {
  const { isVisible, toggleVisibility, isVoiceAvailable } = usePhoneWidget();

  // Legacy layout is icon-only with tooltips on hover — matching the
  // pre-refactor (main-branch) behavior. The PR widened the `.primary-nav`
  // CSS class from 48px to 72px for the new layout's resizable icon +
  // label column, so pin the width back to 48px here via inline style so
  // the flag-off visual matches main exactly.
  return (
    <PrimaryNavIconOnlyContext.Provider value>
      <Flex
        align="center"
        bgColor="tertiary"
        className={classNames(darkModeCss.darkMode, navigationCss.primaryNav)}
        dir="column"
        grow={1}
        justify="space-between"
        px="md"
        py="xl"
        style={{ width: 48, maxWidth: 48 }}
      >
        <Flex align="center" dir="column" gap="lg">
          {!hideTeamSwitcher && <RedoNavigationTeamItem />}
          <Flex dir="column" overflowY="auto">
            <Flex
              dir="column"
              gap="lg"
              overflowY="auto"
              style={{ scrollbarWidth: "none" }}
            >
              {pageTabs.map((tab) => (
                <PrimaryNavigationTab
                  key={tab.id}
                  selectedTabId={selectedTabId}
                  tab={tab}
                />
              ))}
            </Flex>
          </Flex>
        </Flex>
        <Flex align="center" dir="column" gap="lg">
          <PrimaryNavigationTab
            manuallySelected={isCollapseMode}
            tab={{
              id: "collapse",
              UnfilledIcon: LayoutLeft,
              FilledIcon: LayoutLeftFilled,
              type: "button",
              friendlyName: "Collapse",
              hasNotification: false,
              hideLabel: true,
              onClick: toggleCollapseMode,
            }}
          />
          {isVoiceAvailable && (
            <PrimaryNavigationTab
              manuallySelected={isVisible}
              tab={{
                id: "phone",
                UnfilledIcon: PhoneIcon,
                FilledIcon: PhoneFilledIcon,
                type: "button",
                friendlyName: "Phone",
                hasNotification: false,
                hideLabel: true,
                onClick: toggleVisibility,
              }}
            />
          )}
          {enterSettingsUrl && (
            <PrimaryNavigationTab
              tab={{
                id: "settings",
                hasNotification: false,
                UnfilledIcon: GearIcon,
                FilledIcon: GearFilledIcon,
                type: "link",
                href: enterSettingsUrl,
                hideLabel: true,
                friendlyName: "Settings",
                pathRoot: settingsRoot,
              }}
            />
          )}
          <UserProfile collapsed />
        </Flex>
      </Flex>
    </PrimaryNavIconOnlyContext.Provider>
  );
});
